// src/components/oracle/OracleCardDetail.js
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { oracleDeck, uiText } from '../../data/oracle/oracleDeckData';
import { ArrowLeftIcon, TagIcon } from '@heroicons/react/24/solid';
import { useGlow } from '../../hooks/useGlow';
import LinktreeButton from './LinktreeButton';

const memenuItems = [
  {
    title: "Lectura Estrella",
    subtitle: "Obtén una lectura gratis para un tema específico",
    cardCount: 5,
    layout: 'cross',
    path: "/oracle/reading/star",
    featured: true,
  },
];

const OracleCardDetail = ({ lang = 'es', card = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const selectedCard = card || oracleDeck.find(c => c.id.toString() === id) || oracleDeck.find(c => c.idName?.toString()?.toUpperCase() === id?.toUpperCase());
  const ui = uiText[lang] || uiText['es'];

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  const titleStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #9b977aff, #9e823cff, #776f5cff)', intensityMin: 0.2, intensityMax: 0.4, intervalDuration: 3000 });
  const categoryStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #d82995ff, #bf40c4ff, #af52d4ff)', intensityMin: 0.2, intensityMax: 0.4, intervalDuration: 3000 });
  const symbolsDescStyle = useGlow({ gradient: 'linear-gradient(to bottom, rgb(240, 181, 181), rgb(201, 172, 190), rgb(169, 131, 170), rgb(89, 19, 117))', intensityMin: 0.2, intensityMax: 0.4, intervalDuration: 3000 });
  const phraseStyle = useGlow({ gradient: 'linear-gradient(to bottom, #559aafff, #6139ceff, #564bb9ff, rgb(8, 26, 228))', intensityMin: 0.2, intensityMax: 0.4, intervalDuration: 3000 });
  const symbolStyle = useGlow({ gradient: 'linear-gradient(to bottom, #e0e0e0ff, #a3a190ff, #e4d09fff, #c9bea3ff)', intensityMin: 0.2, intensityMax: 0.4, intervalDuration: 3000 });

  if (!selectedCard) return <div className="text-white text-center mt-20">Card not found</div>;

  const cardContent = selectedCard[lang];

  /**
   * Función para procesar Markdown básico:
   * ***texto*** -> Negrita + Cursiva
   * **texto**   -> Negrita
   * *texto*    -> Cursiva
   * [texto](url) -> Link
   */
  const parseMarkdown = (text) => {
    if (typeof text !== 'string') return text;

    const parts = text.split(/(\*\*\*.*?\*\*\*|\*\*.*?\*\*|\*.*?\*|\[[^\]]+\]\([^)]+\))/g);

    return parts.map((part, index) => {
      if (part.startsWith('***') && part.endsWith('***')) {
        return <strong key={index}><em>{part.slice(3, -3)}</em></strong>;
      } else if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      } else if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={index}>{part.slice(1, -1)}</em>;
      } else if (part.startsWith('[') && part.includes('](') && part.endsWith(')')) {
        const match = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (match) {
          const linkText = match[1];
          const url = match[2];
          return (
            <a 
              key={index} 
              href={url} 
              target="" 
              rel="noopener noreferrer"
              className="text-sky-400 hover:text-sky-300 underline underline-offset-2 transition-colors duration-200"
            >
              {linkText}
            </a>
          );
        }
      }
      return part;
    });
  };

  const renderParagraphs = (content, className) => {
    if (!content) return null;
    const paragraphs = Array.isArray(content) ? content : [content];
    return paragraphs.map((text, index) => (
      <p key={index} className={className}>
        {parseMarkdown(text)}
      </p>
    ));
  };

  const renderKeywords = (keywords) => {
    if (!keywords) return null;
    const keywordList = Array.isArray(keywords) ? keywords : keywords.split(',').map(k => k.trim());
    return (
        <>
            <div className="flex flex-wrap gap-2 mt-8 mb-4">
                <div className="flex items-center text-purple-400 mr-2">
                    <TagIcon className="w-4 h-4 mr-1" />
                    <span className="text-xs uppercase font-bold tracking-wider opacity-80">Keywords:</span>
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-6 mb-8">
                {keywordList.map((kw, idx) => (
                    <span key={idx} className="px-3 py-1 bg-purple-900/40 border border-purple-500/30 rounded-full text-xs sm:text-sm text-purple-200 shadow-[0_0_10px_rgba(168,85,247,0.2)] hover:bg-purple-800/60 transition-all duration-300">
                        {kw}
                    </span>
                ))}
            </div>
        </>
    );
  };

  const renderPhrasesAndExplanations = (content) => {
    const indices = new Set();
    Object.keys(content).forEach(key => {
      if (key.startsWith('phrase') || key.startsWith('explanation') || key.startsWith('image')) {
        if (key === 'image' && !content.phrase && !content.explanation) return;
        
        const numStr = key.replace('phrase', '').replace('explanation', '').replace('image', '');
        indices.add(numStr === '' ? 1 : parseInt(numStr, 10));
      }
    });

    return Array.from(indices).sort((a, b) => a - b).map((idx, arrayIndex) => {
      const suffix = idx === 1 ? '' : idx;
      const phrase = content[`phrase${suffix}`];
      const explanation = content[`explanation${suffix}`];
      const sectionImage = content[`image${suffix}`];
      
      const isEven = arrayIndex % 2 !== 0;

      return (
        <div key={`section-group-${idx}`} className="mt-12 flow-root">
          {phrase && (
            <p 
              style={phraseStyle} 
              className="text-xl md:text-2xl font-bold mb-6 text-center md:text-left w-full"
            >
              {parseMarkdown(phrase)}
            </p>
          )}

          <div className="w-full">
            {sectionImage && (
              <img 
                src={sectionImage} 
                alt={phrase || ""} 
                className={`
                  w-full md:w-72 h-auto object-cover rounded-xl border border-purple-500/30 shadow-lg 
                  mb-4 
                  ${isEven 
                    ? 'md:float-right md:ml-8' 
                    : 'md:float-left md:mr-8'
                  }
                `}
              />
            )}
            
            <div className="text-lg text-gray-300 leading-relaxed text-justify">
              {renderParagraphs(explanation)}
            </div>
          </div>
        </div>
      );
    });
  };

  const renderInfoButtons = (content) => {
    const indices = new Set();
    Object.keys(content).forEach(key => {
      if (key.startsWith('infoUrl') && !key.startsWith('infoUrlName') && !key.startsWith('infoUrlImage')) {
        const numStr = key.replace('infoUrl', '');
        indices.add(numStr === '' ? 1 : parseInt(numStr, 10));
      }
    });

    return (
      <div className="flex flex-col gap-4 items-center md:items-start">
        {Array.from(indices).sort((a, b) => a - b).map(idx => {
          const suffix = idx === 1 ? '' : idx;
          const url = content[`infoUrl${suffix}`];
          const name = content[`infoUrlName${suffix}`];
          const imageUrl = content[`infoUrlImage${suffix}`];

          if (!url) return null;

          return (
            <a 
              key={idx} 
              href={url} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ 
                ...phraseStyle, 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '15px', 
                padding: imageUrl ? '8px 30px 8px 10px' : '12px 30px', 
                borderRadius: '50px', 
                textDecoration: 'none', 
                color: '#ffffff', 
                fontWeight: 'bold', 
                fontSize: '13px', 
                letterSpacing: '1.5px', 
                textTransform: 'uppercase', 
                border: '1px solid rgba(255, 255, 255, 0.2)', 
                boxShadow: '0 8px 15px rgba(0,0,0,0.3)', 
                transition: 'all 0.3s ease', 
                cursor: 'pointer', 
                width: 'fit-content' 
              }}
            >
              {imageUrl && (
                <img 
                  src={imageUrl} 
                  alt="" 
                  className="w-10 h-10 rounded-full object-cover border border-white/20 shadow-inner"
                />
              )}
              
              <span style={categoryStyle}>{name || ui.moreInfo}</span>
              <div className="bg-white/15 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">→</div>
            </a>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4 md:p-8 text-white animate-fade-in">
      <div className="relative bg-gray-900/70 border border-purple-500/50 rounded-2xl shadow-2xl shadow-purple-500/20 backdrop-blur-md p-6 md:p-10">
        
        {/* Botón Volver */}
        <button 
          onClick={() => navigate(lang === 'es' ? '/oraculo' : '/oracle')} 
          className="absolute top-4 left-4 flex items-center gap-2 py-2 px-4 bg-gray-800 hover:bg-gray-700 rounded-lg font-semibold text-gray-300 transition-all z-20"
        >
          <ArrowLeftIcon className="w-5 h-5" /><span>{ui.goBack}</span>
        </button>

        {/* CONTENEDOR PRINCIPAL CON FLOW-ROOT */}
        <div className="mt-16 md:mt-4 flow-root">
          
          {/* 1. IMAGEN PRINCIPAL FLOTANTE */}
          <div className="w-full md:w-72 lg:w-80 md:float-left md:mr-8 mb-6 flex justify-center md:block">
            <img 
              src={selectedCard.image} 
              alt={cardContent.name} 
              className="w-full max-w-[320px] md:max-w-none h-auto object-contain rounded-xl border-2 border-sky-400/50 shadow-lg shadow-sky-500/20"
            />
          </div>

          {/* 2. TEXTO QUE RODEA LA IMAGEN (Nombre, Desc y Keywords) */}
          <div className="text-center md:text-left">
            <span style={categoryStyle} className="text-sm font-bold text-purple-400 uppercase tracking-widest block mb-2">
              {cardContent.category}
            </span>
            
            <h1 style={titleStyle} className="text-3xl sm:text-4xl lg:text-5xl font-bold">
              <span className="mr-2 font-light">#{selectedCard.id}</span>
            </h1>

            {cardContent.name && <h1 style={titleStyle} className="text-3xl sm:text-4xl lg:text-5xl font-bold my-2">{cardContent.name}</h1>}
            {cardContent.name2 && <h1 style={titleStyle} className="text-2xl sm:text-3xl lg:text-4xl font-bold my-2">{cardContent.name2}</h1>}
            {cardContent.name3 && <h1 style={titleStyle} className="text-2xl sm:text-3xl lg:text-4xl font-bold my-2">{cardContent.name3}</h1>}

            {/* Las Keywords quedan al costado si hay espacio */}
            {renderKeywords(cardContent.keywords)}
            
            <div className="mt-6">
              {renderParagraphs(cardContent.description, "text-lg text-gray-300 leading-relaxed mb-4 text-justify")}

              {/* BOTÓN OBTENER LECTURA GRATIS CON ESTILOS DE renderInfoButtons */}
              <div className="mt-6 mb-4 flex justify-center md:justify-start">
                <button
                  type="button"
                  onClick={() => navigate(lang === 'es' ? '/oraculo/lectura/estrella' : '/oracle/reading/star')}
                  style={{ 
                    ...phraseStyle, 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '15px', 
                    padding: '12px 30px', 
                    borderRadius: '50px', 
                    textDecoration: 'none', 
                    color: '#ffffff', 
                    fontWeight: 'bold', 
                    fontSize: '13px', 
                    letterSpacing: '1.5px', 
                    textTransform: 'uppercase', 
                    border: '1px solid rgba(255, 255, 255, 0.2)', 
                    boxShadow: '0 8px 15px rgba(0,0,0,0.3)', 
                    transition: 'all 0.3s ease', 
                    cursor: 'pointer', 
                    width: 'fit-content' 
                  }}
                >
                  <span style={categoryStyle}>
                    {lang === 'es' ? 'Obtener lectura gratis' : 'Get free reading'}
                  </span>
                  <div className="bg-white/15 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">
                    →
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* 3. SECCIÓN DE SÍMBOLOS - FORZADA ABAJO CON CLEAR-BOTH */}
          <div className="clear-both pt-8">
            {cardContent.symbolsTitle && (
              <>
                <p style={categoryStyle} className="text-sm font-bold text-purple-400 uppercase tracking-widest">
                  {ui.cardSymbols}
                </p>
                <p style={symbolsDescStyle} className="text-2xl md:text-4xl font-bold my-2 mt-4">
                  {cardContent.symbolsTitle}
                </p>
                {renderParagraphs(cardContent.symbolsExplanation, "text-lg text-gray-300 leading-relaxed mt-2 mb-4 text-justify")}
              </>
            )}
          </div>
        </div>

        {/* SECCIONES DINÁMICAS (PHRASES, EXPLANATIONS, IMAGES EXTRA) */}
        <div className="border-t border-gray-800">
          {renderPhrasesAndExplanations(cardContent)}
        </div>

        {/* BOTONES DE INFORMACIÓN */}
        {renderInfoButtons(cardContent)}
        
        <div className="mt-12 pt-2">
          {memenuItems.map((item, index) => (
            <LinktreeButton key={item.path + index} item={item} index={index} onButtonClick={() => {}} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OracleCardDetail;